import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 25;

const groupByKribhco = (rows) => {
  const map = new Map();
  rows.forEach(row => {
    if (!map.has(row.id)) {
      map.set(row.id, {
        ...row,
        gst_details: [],
      });
    }
    // Only push GST details if gst_id exists (avoid empty rows)
    if (row.gst_id) {
      map.get(row.id).gst_details.push({
        gst_id: row.gst_id,
        gst_number: row.gst_number,
        state: row.gst_state, // Use gst_state to differentiate from main state
        state_code: row.gst_state_code, // Use gst_state_code to differentiate
        address: row.address,
        pincode: row.pincode,
        pan_no: row.pan_no,
        cin_no: row.cin_no,
        email: row.email,
        phone: row.phone,
        account_no: row.account_no,
        ifsc_code: row.ifsc_code,
        bank_name: row.bank_name,
        bank_address: row.bank_address,
        account_holder_name: row.account_holder_name,
        branch: row.branch,
        bank_state: row.bank_state,
        branch_name: row.branch_name,
        branch_code: row.branch_code,
        gst_created_at: row.gst_created_at,
        gst_updated_at: row.gst_updated_at,
      });
    }
  });
  return Array.from(map.values());
};

const KribhcoList = () => {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim()
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/kribhco-get?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      // Group by Kribhco record
      setRecords(groupByKribhco(data.data || []));
      setTotal(data.total || 0);
    } catch (error) {
      setError('Failed to load data');
      console.error('Fetch kribhco error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const openModal = (record) => {
    setSelectedRecord(record);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedRecord(null);
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Kribhco Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            onClick={() => navigate('/kribhco/new')}
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No Kribhco records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Business Name</th>
                <th className="px-3 py-2 text-left font-medium">Entity Type</th>
                <th className="px-3 py-2 text-left font-medium">Registration Type</th>
                <th className="px-3 py-2 text-left font-medium">Nature of Business</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-left font-medium">State Code</th>
                <th className="px-3 py-2 text-left font-medium">CIN No</th>
                <th className="px-3 py-2 text-left font-medium">PAN No</th>
                <th className="px-3 py-2 text-left font-medium">GST Numbers</th>
                <th className="px-3 py-2 text-left font-medium">Email</th>
                <th className="px-3 py-2 text-left font-medium">Phone</th>
                <th className="px-3 py-2 text-left font-medium">AD Code</th>
                <th className="px-3 py-2 text-left font-medium">IEC Code</th>
                <th className="px-3 py-2 text-left font-medium">Created At</th>
                <th className="px-3 py-2 text-left font-medium">Bank Details</th>
                <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1 font-semibold">{r.business_name}</td>
                  <td className="px-3 py-1">{r.entity_type || '-'}</td>
                  <td className="px-3 py-1">{r.registration_type || '-'}</td>
                  <td className="px-3 py-1">{r.nature_of_business || '-'}</td>
                  {/* Fixed: Now shows the main state from kribhco_details */}
                  <td className="px-3 py-1">{r.state || '-'}</td>
                  <td className="px-3 py-1">{r.state_code || '-'}</td>
                  {/* Fixed: Now correctly shows CIN No from first GST detail */}
                  <td className="px-3 py-1">{r.gst_details[0]?.cin_no || '-'}</td>
                  {/* Fixed: Now correctly shows PAN No from first GST detail */}
                  <td className="px-3 py-1">{r.gst_details[0]?.pan_no || '-'}</td>
                  {/* Fixed: Now correctly shows GST numbers */}
                  <td className="px-3 py-1">{r.gst_details.map(g => g.gst_number).filter(Boolean).join(', ') || '-'}</td>
                  {/* Fixed: Now correctly shows email from first GST detail */}
                  <td className="px-3 py-1">{r.gst_details[0]?.email || '-'}</td>
                  {/* Fixed: Now correctly shows phone from first GST detail */}
                  <td className="px-3 py-1">{r.gst_details[0]?.phone || '-'}</td>
                  <td className="px-3 py-1">{r.ad_code || '-'}</td>
                  <td className="px-3 py-1">{r.iec_code || '-'}</td>
                  <td className="px-3 py-1">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</td>
                  <td className="px-3 py-1">
                    <button
                      className="px-2 py-1 rounded text-xs font-medium border"
                      style={{ background: '#10a5e9', color: '#fff', borderColor: '#10a5e9' }}
                      onMouseOver={e => { e.currentTarget.style.background = '#0e8dc6'; }}
                      onMouseOut={e => { e.currentTarget.style.background = '#10a5e9'; }}
                      onClick={() => openModal(r)}
                    >
                      View Bank Details
                    </button>
                  </td>
                  <td className="px-3 py-1">
                    <button
                      onClick={() => navigate(`/kribhco/edit/${r.id}`)}
                      className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8ac2] border border-[#10a5e9] flex items-center gap-1"
                      title="Edit Kribhco"
                    >
                      <FaEdit />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrev}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}

      {/* Modal for Bank Details */}
      {modalOpen && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded shadow-lg max-w-2xl w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-lg"
              onClick={closeModal}
            >
              &times;
            </button>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#10a5e9' }}>
              Bank & GST Details for <span>{selectedRecord.business_name}</span>
            </h3>
            
            {/* Show main state information first */}
            <div className="border rounded p-3 bg-blue-50 mb-4">
              <h4 className="font-medium text-sm mb-2" style={{ color: '#10a5e9' }}>Main Business Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div><span className="font-semibold" style={{ color: '#10a5e9' }}>State:</span> {selectedRecord.state || '-'}</div>
                <div><span className="font-semibold" style={{ color: '#10a5e9' }}>State Code:</span> {selectedRecord.state_code || '-'}</div>
                <div><span className="font-semibold" style={{ color: '#10a5e9' }}>AD Code:</span> {selectedRecord.ad_code || '-'}</div>
                <div><span className="font-semibold" style={{ color: '#10a5e9' }}>IEC Code:</span> {selectedRecord.iec_code || '-'}</div>
                <div><span className="font-semibold" style={{ color: '#10a5e9' }}>FSSAI No:</span> {selectedRecord.fssai_no || '-'}</div>
              </div>
            </div>

            {selectedRecord.gst_details.length === 0 ? (
              <div className="text-gray-500">No GST/Bank details found.</div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <h4 className="font-medium text-sm" style={{ color: '#10a5e9' }}>GST & Bank Details</h4>
                {selectedRecord.gst_details.map((g, idx) => (
                  <div key={g.gst_id || idx} className="border rounded p-3 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>GST Number:</span> {g.gst_number || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>GST State:</span> {g.state || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>GST State Code:</span> {g.state_code || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Address:</span> {g.address || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Pincode:</span> {g.pincode || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>PAN No:</span> {g.pan_no || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>CIN No:</span> {g.cin_no || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Email:</span> {g.email || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Phone:</span> {g.phone || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Account Number:</span> {g.account_no || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>IFSC Code:</span> {g.ifsc_code || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Bank Name:</span> {g.bank_name || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Bank Address:</span> {g.bank_address || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Account Holder:</span> {g.account_holder_name || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Branch:</span> {g.branch || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Bank State:</span> {g.bank_state || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Branch Name:</span> {g.branch_name || '-'}</div>
                      <div><span className="font-semibold" style={{ color: '#10a5e9' }}>Branch Code:</span> {g.branch_code || '-'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KribhcoList;
